/* eg-preset-configurator.js
   Standalone configurator JS — replaces theme-editor.js for the preset template.
   - Reads tag-driven complexity (caps, stones count, custom design tier)
   - Customer picks: Position, Metal, StoneType (if has_stones), PreciousStone (if has_precious)
   - Calculates total live, submits to /apps/create-variant
   - Replaces alert() popup with inline toast
*/
(() => {
  function ready(fn){document.readyState!=='loading'?fn():document.addEventListener('DOMContentLoaded',fn);}

  // Plain integer format: $1234 (no comma, no decimals)
  function fmtUSD(n){ return '$' + Math.round(Number(n) || 0); }

  // ---------- pricing fallback (used when PRICING_MATRIX metafield missing) ----------
  const FALLBACK_GOLD = {
    Top:    { Silver:75, '10K':229, '14K':369, '18K':538 },
    Bottom: { Silver:75, '10K':176, '14K':284, '18K':414 },
  };
  // Stone retail per-stone prices (sourced from master sheet "Rtl Diam Prc" tab @ 1.3mm row)
  //  - Moissanite: WH Diam Price wholesale × 10 ($0.5 → $5)
  //  - Lab Grown: Rtl Diam Prc "Commercial Diamond" column ($7)
  //  - Diamonds: Rtl Diam Prc SI/VS/VVS columns ($9/$12/$15)
  const STONE_PRICES = {
    'moissanite':   5,
    'lab-grown':    7,
    'diamond-si':   9,
    'diamond-vs':  12,
    'diamond-vvs': 15,
  };
  // Sale tape (visual compare-at) — auto-derived from baseline metafield.
  // Activation: theme setting `egpc_sale_enabled` (window.GTU_SALE_ENABLED) + gtu.pricing_baseline
  // metafield (window.GTU_BASELINE) seeded via `node snapshotBaseline.js --from-current --pct=N --push`.
  // Assumption: baseline scales every rate by the same ratio (uniform sitewide claim).
  // Cart price is NEVER reduced — this is visual only; the variant is created at sheet-rate price.
  function compareAtMultiplier(){
    if (!window.GTU_SALE_ENABLED) return 0;
    const b = window.GTU_BASELINE, m = window.PRICING_MATRIX;
    if (!b || !m || !b.Top || !m.Top) return 0;
    const baseRef = Number(b.Top['10K']);
    const currRef = Number(m.Top['10K']);
    if (!baseRef || !currRef || baseRef <= currRef) return 0;
    return baseRef / currRef;
  }
  const COMPARE_AT_MULT = compareAtMultiplier();
  const CLAIMED_PCT = (window.GTU_BASELINE && Number(window.GTU_BASELINE.claimedPercent)) || 0;
  // Fallback Lab prices per gem — used only if window.PRICING_MATRIX.Precious is missing.
  // Live values come from the synced master sheet "WH Diam Price" tab via metafield gtu.pricing_matrix.
  // Rule: sheet wholesale = retail Lab; frontend multiplies × 2 for Genuine.
  const PRECIOUS_PRICES = {
    opal:              120,
    quartz:            160,
    sapphire:          160,
    ruby:              160,
    emerald:           160,
    topaz:             160,
    onyx:              160,
    amethyst:          160,
    'mother-of-pearl': 160,
    citrine:           160,
    'blue-lapis':      160,
  };
  const CUSTOM_PRICES   = { 1:20, 2:50, 3:100, 4:200, 5:300, 6:500, 7:600, 8:800, 9:1000 };
  // Fang per-tooth pricing — from master sheet "Retail Price" tab row 16-20.
  // Matrix: size × metal karat tier. Sheet groups "Low Gold" (Silver) with 10K, 14K, 18K+.
  // 22K and Platinum fall back to 18K rate (sheet doesn't break them out for fangs).
  const FANG_PRICES = {
    small:  { Silver: 20, '10K': 20, '14K': 30, '14KT': 30, '18K': 40, '18KT': 40, '22K': 40, Platinum: 40 },
    long:   { Silver: 40, '10K': 40, '14K': 50, '14KT': 50, '18K': 60, '18KT': 60, '22K': 60, Platinum: 60 },
    curved: { Silver: 50, '10K': 50, '14K': 60, '14KT': 60, '18K': 70, '18KT': 70, '22K': 70, Platinum: 70 },
  };
  function fangPrice(size, metal){
    const m = karatKey(metal);
    // Live values from synced metafield first; fall back to hardcoded if missing.
    const M = window.PRICING_MATRIX;
    if (M && M.Fangs && M.Fangs[size]) {
      const sm = M.Fangs[size];
      return sm[m] != null ? sm[m] : (sm['18K'] || 0);
    }
    const sizeMap = FANG_PRICES[size];
    if (!sizeMap) return 0;
    return sizeMap[m] != null ? sizeMap[m] : (sizeMap['18K'] || 0);
  }

  // Gap Filler per-tooth pricing — master sheet "Retail Price" row 16-20 col L onward.
  // Sheet groups Low Gold (Silver) WITH 10K (different from fangs which keep them separate).
  // Adds 22K as its own tier.
  const GAP_PRICES = {
    small:  { Silver: 20, '10K': 20, '14K':  40, '14KT':  40, '18K':  60, '18KT':  60, '22K':  80, Platinum:  80 },
    long:   { Silver: 50, '10K': 50, '14K':  80, '14KT':  80, '18K': 100, '18KT': 100, '22K': 120, Platinum: 120 },
    curved: { Silver: 70, '10K': 70, '14K': 100, '14KT': 100, '18K': 120, '18KT': 120, '22K': 140, Platinum: 140 },
  };
  function gapPrice(size, metal){
    const m = karatKey(metal);
    // Live values from synced metafield first; fall back to hardcoded if missing.
    const M = window.PRICING_MATRIX;
    if (M && M.Gaps && M.Gaps[size]) {
      const sm = M.Gaps[size];
      return sm[m] != null ? sm[m] : (sm['18K'] || 0);
    }
    const sizeMap = GAP_PRICES[size];
    if (!sizeMap) return 0;
    return sizeMap[m] != null ? sizeMap[m] : (sizeMap['18K'] || 0);
  }
  function gapLabel(s){return s === 'long' ? 'Long' : (s === 'curved' ? 'Curved' : 'Small');}

  function karatKey(v){const s=String(v||'').toLowerCase();if(s==='10k')return '10K';if(s==='14k')return '14K';if(s==='18k')return '18K';if(s==='silver')return 'Silver';return v;}
  function stoneLabel(key){
    const m = { 'moissanite':'Moissanite', 'lab-grown':'Lab Grown', 'diamond-si':'Diamond SI', 'diamond-vs':'Diamond VS', 'diamond-vvs':'Diamond VVS' };
    return m[key] || key;
  }
  function perCap(pos, metal){
    const k = karatKey(metal);
    const p = (pos === 'Bottom') ? 'Bottom' : 'Top';
    const M = window.PRICING_MATRIX;
    if (M && M[p] && M[p][k] != null) return Number(M[p][k]);
    if (FALLBACK_GOLD[p] && FALLBACK_GOLD[p][k] != null) return Number(FALLBACK_GOLD[p][k]);
    return null;
  }

  // Precious stone LAB price (Genuine = 2× Lab — done by caller).
  // Reads from synced metafield first (M.Precious), falls back to hardcoded PRECIOUS_PRICES.
  function preciousPrice(gem){
    if (!gem) return 0;
    const M = window.PRICING_MATRIX;
    if (M && M.Precious && M.Precious[gem] != null) return Number(M.Precious[gem]);
    return Number(PRECIOUS_PRICES[gem]) || 0;
  }

  // ---------- tag parsing ----------
  function escapeReg(s){return String(s).replace(/[.*+?^${}()|[\]\\]/g,'\\$&');}
  // Reads `Custom Design $150` / `Custom $2,000` and returns the number after the '$'.
  // Returns 0 when no such tag exists, so the caller falls back to the tier table.
  function customDollarTag(){
    const tags = (window.productTags||[]).map(t=>String(t).toLowerCase());
    for (const raw of tags){
      if (raw.indexOf('custom') === -1) continue;
      const m = raw.match(/\$\s*([\d,]+)/);
      if (m && m[1]){
        const n = parseInt(m[1].replace(/,/g,''), 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return 0;
  }

  // True for `Custom Design $125/tooth` — a PER-CAP design charge.
  //
  // WHY: a flat amount cannot describe a configurable set. The Rose Gold Constellation
  // carries 5 carats of stone across 16 caps for $2,000; someone who configures only the
  // top 8 should pay $1,000, not the full $2,000. Written per tooth, the charge scales
  // with the selection on its own. A tag with no /tooth stays flat, as before.
  function customIsPerTooth(){
    const tags = (window.productTags||[]).map(t=>String(t).toLowerCase());
    for (const raw of tags){
      if (raw.indexOf('custom') === -1) continue;
      if (/\$\s*[\d,]+\s*\/\s*(tooth|cap)/.test(raw)) return true;
    }
    return false;
  }

  function tagNumber(keys){
    const tags = (window.productTags||[]).map(t=>String(t).toLowerCase());
    for (const raw of tags){
      for (const k of keys){
        const key = String(k).toLowerCase();
        let m = raw.match(new RegExp(`(\\d+)\\s*${escapeReg(key)}`));
        if (m && m[1]) return parseInt(m[1],10);
        m = raw.match(new RegExp(`${escapeReg(key)}[\\s:_-]*(\\d+)`));
        if (m && m[1]) return parseInt(m[1],10);
      }
    }
    return 0;
  }

  // ---------- state ----------
  // Defaults per operator spec:
  //   Position: Top, Metal: Silver, GoldColor: yellow,
  //   Stone (if has stones): Moissanite, Precious (if has precious): Opal,
  //   Plating (if Silver): none (Yellow plating adds $15/tooth)
  //
  // Metal default is Silver (2026-08-20, operator decision). The catalog card advertises
  // "Silver price from $X", so the PDP must load on the SAME metal or the number jumps
  // the moment the customer clicks through. Silver is the floor, so the price only ever
  // goes UP from here as they upgrade — never down.
  //
  // CAPITALIZATION IS LOAD-BEARING: it must be 'Silver' with a capital S, matching
  // data-metal="Silver" on the pill in eg-preset-configurator.liquid. Gold tiers are
  // lowercase ('10k','14k','18k') and get normalized by karatKey(), but Silver is
  // compared with STRICT EQUALITY in five places — platingTotal, the gold-color section
  // toggle, the plating section toggle, and the GoldColor / Plating line item properties.
  // Lowercase 'silver' would still price correctly (karatKey normalizes it) while
  // silently breaking all five: a Silver piece would ship with a GoldColor property.
  //
  // This default is paired with the `active` class on the Silver pill in the Liquid.
  // Change one without the other and the DOM highlights one metal while state prices
  // another — init() syncs position/nugget/cut/substyle from the DOM, but NOT metal.
  const state = { position:'Top', metal:'Silver', goldColor:'yellow', plating:'none', fangSize:'small', gapSize:'small', stone:'moissanite', stoneTier:'moissanite', precious:null, preciousType:'lab', teeth: new Set(), nuggetSize:'regular', cutPattern:'criss-cross', substyle:'solid' };
  // Nugget per-tooth multiplier (matches Custom Builder line 59).
  const NUGGET_MULT = { mini: 0.15, regular: 0.20, bulky: 0.30 };
  // Stones-per-tooth driven by anatomical position + product style.
  // Operator-confirmed values 2026-06-09:
  //   - Honeycomb (fully iced pavé): R3-L3 = 35, R4-L5 = 42, R6-L7 = 49 (back teeth bigger)
  //   - Open Face + Diamond outline: ~35% of honeycomb (just bordering, not full surface)
  //   - Opal Center: same outline ring as open-face; opal itself counted as precious gem
  const STONES_PER_TOOTH_BY_STYLE = {
    'honeycomb':         { incisor: 35, premolar: 42, molar: 49 },
    'openface-outline':  { incisor: 12, premolar: 15, molar: 18 },
    'opal-center':       { incisor: 12, premolar: 15, molar: 18 },
  };
  // Classify a tooth ID (e.g., "Top-R3", "Bottom-L7") into anatomical group.
  // R1/R2/R3 + L1/L2/L3 = incisors+canines (front 6); R4/R5/L4/L5 = premolars; R6/R7/L6/L7 = molars.
  function toothGroup(id){
    const m = String(id || '').match(/-([RL])(\d+)$/);
    if (!m) return 'incisor';
    const n = parseInt(m[2], 10);
    if (n <= 3) return 'incisor';
    if (n <= 5) return 'premolar';
    return 'molar';
  }
  // Sum stones across selected teeth using the style's per-position table.
  function stoneCountFromTeeth(teethSet, style){
    const table = STONES_PER_TOOTH_BY_STYLE[style];
    if (!table || !teethSet || teethSet.size === 0) return 0;
    let total = 0;
    teethSet.forEach(id => { total += (table[toothGroup(id)] || 0); });
    return total;
  }
  // Compute the max teeth a customer may select. Tag-driven (back-compat)
  // when neither Configurable Caps nor Single Tooth Only tag present.
  function maxCapsForPosition(){
    if (window.__HAS_SINGLE_ONLY__) return 1;
    if (window.__HAS_CONFIGURABLE_CAPS__){
      if (state.position === 'Single') return 1;
      if (state.position === 'Full') return 24;
      return 12;
    }
    return tagNumber(['caps','cap','gaps','gap']) || 1;
  }
  function minTeethForPosition(){
    if (window.__HAS_SINGLE_ONLY__) return 1;
    if (window.__HAS_CONFIGURABLE_CAPS__){
      if (state.position === 'Full') return 6;
      return 1;
    }
    return tagNumber(['caps','cap','gaps','gap']) || 1;
  }
  // Default teeth pre-selection plan for a FRESH visit. Precedence:
  //   1) explicit "Default: N Position" tag (e.g. "Default: 6 Top")
  //   2) single-tooth-only products -> 1 (Single)
  //   3) configurable-caps products (build-your-piece) -> 6 Top
  //   4) fixed-count classic products -> auto-fill their exact N caps/gaps
  // Returns null when nothing should be pre-selected. The +/- limits are unchanged;
  // this only moves the starting point from "empty" to "N already selected".
  function defaultTeethPlan(){
    const tags = (window.productTags||[]).map(t=>String(t).toLowerCase());
    for (const t of tags){
      const m = t.match(/default[\s:_-]*(\d+)\s*(top|bottom|full|single)?/);
      if (m && m[1]){
        const p = m[2] ? m[2].charAt(0).toUpperCase()+m[2].slice(1) : 'Top';
        return { count: parseInt(m[1],10), position: p };
      }
    }
    if (window.__HAS_SINGLE_ONLY__) return { count: 1, position: 'Single' };
    if (window.__HAS_CONFIGURABLE_CAPS__) return { count: 6, position: 'Top' };
    const fixed = tagNumber(['caps','cap','gaps','gap']);
    if (fixed > 0) return { count: fixed, position: fixed > 12 ? 'Full' : 'Top' };
    return null;
  }
  // Center-out tooth order within an arch, so a default of N fills the most visible
  // front teeth first. Full interleaves top+bottom outward from the center.
  function orderedTeeth(position, count){
    const arch = ['R1','L1','R2','L2','R3','L3','R4','L4','R5','L5','R6','L6'];
    const ids = [];
    if (position === 'Bottom'){ for (const s of arch) ids.push('Bottom-'+s); }
    else if (position === 'Full'){ for (const s of arch){ ids.push('Top-'+s); ids.push('Bottom-'+s); } }
    else { for (const s of arch) ids.push('Top-'+s); } // Top or Single
    return ids.slice(0, Math.max(0, count));
  }
  function fangLabel(s){return s === 'long' ? 'Long' : (s === 'curved' ? 'Curved' : 'Small');}
  const PLATING_PRICE = 15;        // $/tooth for Yellow plating on Silver
  const GENUINE_MULTIPLIER = 2;    // Genuine precious stones = 2× lab price (operator rule: sheet shows lab; genuine doubles)

  // ---------- recompute ----------
  function recompute(){
    // Caps: derived from teeth selection when the product is Configurable Caps
    // or Single Tooth Only; otherwise fall back to the tag-driven count.
    let caps;
    if (window.__HAS_CONFIGURABLE_CAPS__ || window.__HAS_SINGLE_ONLY__){
      caps = state.teeth.size || 0;
    } else {
      caps = tagNumber(['caps','cap']) || 1;
    }
    // Stone count: if product declares a style (Honeycomb / Open Face Outline / Opal Center),
    // sum stones across selected teeth by anatomical position. Otherwise legacy fixed tag count.
    let stoneCount;
    if (window.__STYLE_KEY__ && STONES_PER_TOOTH_BY_STYLE[window.__STYLE_KEY__]){
      stoneCount = stoneCountFromTeeth(state.teeth, window.__STYLE_KEY__);
    } else {
      stoneCount = window.__HAS_SUBSTYLE__
        ? (tagNumber(['stones','stone']) || 0) * (caps || 1)
        : (tagNumber(['stones','stone']) || 0);
    }
    const customTier = tagNumber(['custom design','custom']) || 0;
    // `Custom Design $150` states the price outright and beats the 1-9 tier table,
    // which can only express nine fixed amounts and never $150, $240 or $2,000.
    // tagNumber() cannot see these: its patterns stop at [\s:_-] and never cross a '$'.
    const customExplicit = customDollarTag();

    // For Full Set, perCap('Full', metal) doesn't exist in FALLBACK_GOLD —
    // sum top + bottom averaged per cap (operator treats Full as top+bottom rates).
    function perCapEffective(pos, metal){
      if (pos === 'Full'){
        const top = perCap('Top', metal) || 0;
        const bot = perCap('Bottom', metal) || 0;
        return (top + bot) / 2;
      }
      if (pos === 'Single') return perCap('Top', metal);
      return perCap(pos, metal);
    }
    const goldPC = perCapEffective(state.position, state.metal);
    const metalTotal = goldPC != null ? goldPC * caps : 0;
    const platingTotal = (state.metal === 'Silver' && state.plating === 'yellow') ? (PLATING_PRICE * caps) : 0;
    // Style uplift per tooth — Nuggets uses the per-cap base × size multiplier.
    // Diamond Cut adds $0 (visual only, per jefe J-05).
    let styleUpliftPerTooth = 0;
    if (window.__HAS_NUGGETS__ && state.nuggetSize){
      styleUpliftPerTooth += (goldPC || 0) * (NUGGET_MULT[state.nuggetSize] || 0);
    }
    const styleUpliftTotal = styleUpliftPerTooth * caps;
    // Fangs — only counts if product has "N Fangs" tag (window.__HAS_FANGS__ set in Liquid)
    const fangCount = window.__HAS_FANGS__ ? (tagNumber(['fangs','fang']) || 1) : 0;
    const fangUnit = fangPrice(state.fangSize, state.metal);
    const fangsTotal = fangCount > 0 ? fangUnit * fangCount : 0;
    if (window.__HAS_FANGS__) {
      document.querySelectorAll('[data-fang-price]').forEach(el => {
        const size = el.dataset.fangPrice;
        el.textContent = '+' + fmtUSD(fangPrice(size, state.metal)) + '/fang';
      });
    }

    // Gap Fillers — count from explicit "N Gaps" / "N Gap" product tag.
    // Falls back to 1 if has_gaps tag detected but no number specified.
    const gapCount = window.__HAS_GAPS__ ? (tagNumber(['gaps','gap']) || 1) : 0;
    const gapUnit = gapPrice(state.gapSize, state.metal);
    const gapsTotal = gapCount > 0 ? gapUnit * gapCount : 0;
    if (window.__HAS_GAPS__) {
      document.querySelectorAll('[data-gap-price]').forEach(el => {
        const size = el.dataset.gapPrice;
        el.textContent = '+' + fmtUSD(gapPrice(size, state.metal)) + '/gap';
      });
    }
    // For Single Tooth products with substyle picker, only charge stones when substyle === 'iced'.
    // For other products, charge stones whenever the product has the tag and a stone is picked.
    const stonesActive = window.__HAS_SUBSTYLE__ ? (state.substyle === 'iced') : true;
    const stonesTotal = (stonesActive && state.stone && stoneCount > 0) ? (STONE_PRICES[state.stone]||0) * stoneCount : 0;
    // Substyle products: only reveal the Stone Type selector when "Iced" is chosen.
    if (window.__HAS_SUBSTYLE__){
      var _stoneSec = document.getElementById('egpc-stone-tier-pills');
      if (_stoneSec){ var _box = _stoneSec.closest('.egpc-section'); if (_box) _box.style.display = (state.substyle === 'iced') ? '' : 'none'; }
      var _grades = document.getElementById('egpc-diamond-grades');
      if (_grades && state.substyle !== 'iced') _grades.style.display = 'none';
    }
    // Precious stone count: if product has "1 Opal Per Cap" / per-cap scaling tag,
    // scale with selected teeth. Otherwise legacy fixed count from tag.
    let preciousCount;
    if (window.__OPAL_PER_CAP__){
      preciousCount = state.teeth.size || 0;
    } else {
      preciousCount = tagNumber(['precious stones','precious stone']) || (window.__HAS_PRECIOUS__ ? 1 : 0);
    }
    const preciousUnit = state.precious ? preciousPrice(state.precious) : 0;
    const preciousBase = preciousUnit * preciousCount;
    const preciousTotal = preciousBase * (state.preciousType === 'genuine' ? GENUINE_MULTIPLIER : 1);
    const customTotal = customExplicit > 0
      ? (customIsPerTooth() ? customExplicit * (caps || 1) : customExplicit)
      : (customTier > 0 ? (CUSTOM_PRICES[customTier]||0) : 0);
    // The FULL price — this is what the variant is created at, and what Shopify
    // applies its automatic discount on top of. No pre-discounting here.
    const subtotal = 40 + metalTotal + platingTotal + styleUpliftTotal + fangsTotal + gapsTotal + stonesTotal + preciousTotal + customTotal;
    // Compare-at: subtotal = sheet rates = what customer pays. Baseline = pre-sale snapshot.
    // baselineSubtotal scales subtotal by the uniform multiplier; if multiplier=0 (sale off
    // or no baseline), strike + savings line hide automatically.
    const compareAt = COMPARE_AT_MULT > 0 ? subtotal * COMPARE_AT_MULT : 0;
    const savings = compareAt > 0 ? compareAt - subtotal : 0;
    const customerTotal = subtotal;
    const total = subtotal;

    // Metal-truth eyebrow above the big price.
    // The product card advertises the Silver floor ("Silver price from $X"), so the PDP
    // has to name the metal the live number actually belongs to. state.metal now defaults
    // to Silver (so this reads "SILVER PRICE FROM" on load, matching the card), and flips
    // on every metal pill click, so this label is rebuilt here rather than hardcoded in
    // Liquid — it can never sit on a gold number saying "Silver".
    // Hidden while caps === 0 (nothing picked yet, the number is just the $40 base).
    // When it shows it takes over the eyebrow slot from "Your build" so the two don't stack.
    const metalLabelEl = document.getElementById('egpc-price-metal-label');
    if (metalLabelEl){
      const showMetalLabel = caps > 0;
      metalLabelEl.hidden = !showMetalLabel;
      if (showMetalLabel) metalLabelEl.textContent = String(karatKey(state.metal) + ' price from').toUpperCase();
      const liveLabelEl = document.getElementById('egpc-price-live-label');
      if (liveLabelEl) liveLabelEl.style.display = showMetalLabel ? 'none' : '';

      // The "Starting at $310" anchor goes with it. Once a build exists, that number is
      // the floor for a ONE-tooth piece while the big number below is the six-tooth
      // build being configured, so leaving both on screen showed the customer two
      // different prices for the same product with nothing explaining the gap. The
      // anchor is useful before anything is picked; after that it only contradicts.
      const anchorEl = document.querySelector('.egpc-price-anchor');
      if (anchorEl) anchorEl.style.display = showMetalLabel ? 'none' : '';
    }

    // Update price displays — show "was" (strikethrough) + current price (bold) when compare-at active
    setText('egpc-total', fmtUSD(customerTotal));
    setText('egpc-bd-total', fmtUSD(customerTotal));
    setText('egpc-stickybar-price', fmtUSD(customerTotal));
    const strikeEl = document.getElementById('egpc-total-strike');
    if (strikeEl) {
      if (compareAt > 0) {
        strikeEl.style.display = 'inline-block';
        strikeEl.textContent = fmtUSD(compareAt);
      } else {
        strikeEl.style.display = 'none';
      }
    }
    const sbStrike = document.getElementById('egpc-stickybar-strike');
    if (sbStrike) {
      if (compareAt > 0) {
        sbStrike.hidden = false;
        sbStrike.textContent = fmtUSD(compareAt);
      } else {
        sbStrike.hidden = true;
      }
    }
    setText('egpc-bd-base', '$40');
    setText('egpc-bd-metal-label', `Metal (${karatKey(state.metal)} × ${caps} caps)`);
    setText('egpc-bd-metal', fmtUSD(metalTotal));

    toggleRow('egpc-bd-fangs-row', fangsTotal > 0);
    if (fangsTotal > 0){
      setText('egpc-bd-fangs-label', `Fangs (${fangLabel(state.fangSize)} × ${fangCount})`);
      setText('egpc-bd-fangs', fmtUSD(fangsTotal));
    }
    toggleRow('egpc-bd-gaps-row', gapsTotal > 0);
    if (gapsTotal > 0){
      setText('egpc-bd-gaps-label', `Gap Fillers (${gapLabel(state.gapSize)} × ${gapCount})`);
      setText('egpc-bd-gaps', fmtUSD(gapsTotal));
    }
    toggleRow('egpc-bd-stones-row', stonesTotal > 0);
    if (stonesTotal > 0){
      setText('egpc-bd-stones-label', `Stones (${stoneLabel(state.stone)} × ${stoneCount})`);
      setText('egpc-bd-stones', fmtUSD(stonesTotal));
    }
    toggleRow('egpc-bd-precious-row', preciousTotal > 0);
    if (preciousTotal > 0){
      const typeLabel = state.preciousType === 'genuine' ? 'Genuine' : 'Lab';
      setText('egpc-bd-precious-label', `Precious (${state.precious} · ${typeLabel} × ${preciousCount})`);
      setText('egpc-bd-precious', fmtUSD(preciousTotal));
    }
    toggleRow('egpc-bd-custom-row', customTotal > 0);
    if (customTotal > 0){
      setText('egpc-bd-custom-label', `Craftsmanship (Level ${customTier})`);
      setText('egpc-bd-custom', fmtUSD(customTotal));
    }
    // Subtotal + savings lines (show when compare-at active)
    toggleRow('egpc-bd-subtotal-row', compareAt > 0);
    toggleRow('egpc-bd-discount-row', compareAt > 0);
    if (compareAt > 0){
      setText('egpc-bd-subtotal', fmtUSD(compareAt));
      setText('egpc-bd-discount-label', `${CLAIMED_PCT || Math.round((savings/compareAt)*100)}% OFF SALE`);
      setText('egpc-bd-discount', '-' + fmtUSD(savings));
    }

    // Update per-cap labels on metal pills
    setText(null, perCap(state.position,'Silver') ? `${fmtUSD(perCap(state.position,'Silver'))}/cap` : '—', '[data-pp-silver]');
    setText(null, perCap(state.position,'10k') ? `${fmtUSD(perCap(state.position,'10k'))}/cap` : '—', '[data-pp-10k]');
    setText(null, perCap(state.position,'14k') ? `${fmtUSD(perCap(state.position,'14k'))}/cap` : '—', '[data-pp-14k]');
    setText(null, perCap(state.position,'18k') ? `${fmtUSD(perCap(state.position,'18k'))}/cap` : '—', '[data-pp-18k]');

    // Enable button when required selections made
    const stoneOk = !window.__HAS_STONES__ || (window.__HAS_SUBSTYLE__ && state.substyle !== 'iced') || !!state.stone;
    const preciousOk = !window.__HAS_PRECIOUS__ || !!state.precious;
    let teethOk;
    if (window.__HAS_CONFIGURABLE_CAPS__){
      teethOk = state.teeth.size >= minTeethForPosition();
    } else if (window.__HAS_SINGLE_ONLY__){
      teethOk = state.teeth.size === 1;
    } else {
      teethOk = state.teeth.size === caps;
    }
    const btn = document.getElementById('egpc-add-btn');
    if (btn){
      const ready = state.position && state.metal && stoneOk && preciousOk && teethOk;
      // Diagnose the FIRST missing requirement (priority matches section order).
      // Button stays clickable when not-ready — click scrolls to the missing section
      // instead of submitting. Driven by Clarity data: customers click variant pills
      // then bail at the generic "Select options to add" without knowing what's missing.
      let missingText = 'Select options to add';
      let missingTarget = '';
      if (!state.position) { missingText = 'Pick top / bottom / single ↑'; missingTarget = 'egpc-position-pills'; }
      else if (!state.metal) { missingText = 'Pick a metal ↑'; missingTarget = 'egpc-metal-pills'; }
      else if (!stoneOk) { missingText = 'Pick a stone type ↑'; missingTarget = 'egpc-stone-tier-pills'; }
      else if (!preciousOk) { missingText = 'Pick a precious gem ↑'; missingTarget = 'egpc-precious-pills'; }
      else if (!teethOk) {
        const need = caps - state.teeth.size;
        missingText = `Pick ${need} more ${need === 1 ? 'tooth' : 'teeth'} ↓`;
        missingTarget = 'egpc-teeth-section';
      }
      btn.classList.toggle('ready', ready);
      btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      btn.removeAttribute('disabled'); // always clickable; onAddClick decides between submit and scroll-to-missing
      if (ready) {
        btn.textContent = `Add to Cart — ${fmtUSD(customerTotal)}`;
        btn.dataset.scrollTarget = '';
      } else {
        btn.textContent = missingText;
        btn.dataset.scrollTarget = missingTarget;
      }
      const sbBtn = document.getElementById('egpc-stickybar-btn');
      if (sbBtn) {
        sbBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
        sbBtn.textContent = ready ? `Add to Cart — ${fmtUSD(customerTotal)}` : missingText;
      }
    }

    // Update teeth counter text
    const counter = document.getElementById('egpc-teeth-counter');
    if (counter){
      const sel = state.teeth.size;
      if (window.__HAS_CONFIGURABLE_CAPS__){
        const minReq = minTeethForPosition();
        const maxAllowed = maxCapsForPosition();
        if (sel === 0) counter.textContent = `Click each tooth that gets a cap (${minReq}-${maxAllowed} ${state.position === 'Full' ? 'across both arches' : ''})`.trim();
        else if (sel < minReq) counter.textContent = `${sel} / ${minReq} minimum — pick ${minReq - sel} more`;
        else counter.textContent = `${sel} ${sel === 1 ? 'tooth' : 'teeth'} selected ✓`;
        counter.classList.toggle('ready', sel >= minReq);
      } else if (window.__HAS_SINGLE_ONLY__){
        if (sel === 0) counter.textContent = `Pick which tooth`;
        else counter.textContent = `${sel} tooth selected ✓`;
        counter.classList.toggle('ready', sel === 1);
      } else {
        if (sel === 0) counter.textContent = `Select ${caps} ${caps === 1 ? 'tooth' : 'teeth'}`;
        else if (sel === caps) counter.textContent = `${sel} / ${caps} selected ✓`;
        else counter.textContent = `${sel} / ${caps} selected — pick ${caps - sel} more`;
        counter.classList.toggle('ready', sel === caps);
      }
    }

    state._calcCache = { caps, stoneCount, customTier, preciousCount, fangCount, gapCount, total };
  }

  function setText(id, text, selector){
    if (id){ const el = document.getElementById(id); if (el) el.textContent = text; return; }
    if (selector){ document.querySelectorAll(selector).forEach(el => el.textContent = text); }
  }
  function toggleRow(id, show){
    const el = document.getElementById(id);
    if (el) el.style.display = show ? 'flex' : 'none';
  }

  // ---------- pill behaviors ----------
  function bindPills(){
    const positionPills = document.querySelectorAll('#egpc-position-pills [data-position]');
    positionPills.forEach(p => p.addEventListener('click', () => {
      positionPills.forEach(x => x.classList.remove('active'));
      p.classList.add('active');
      state.position = p.dataset.position;
      recompute();
    }));

    const metalPills = document.querySelectorAll('#egpc-metal-pills [data-metal]');
    const goldColorSection = document.getElementById('egpc-gold-color-section');
    function toggleGoldColorVisibility(){
      if (!goldColorSection) return;
      goldColorSection.style.display = (state.metal === 'Silver') ? 'none' : 'block';
    }
    toggleGoldColorVisibility(); // initial
    metalPills.forEach(m => m.addEventListener('click', () => {
      metalPills.forEach(x => x.classList.remove('active'));
      m.classList.add('active');
      state.metal = m.dataset.metal;
      toggleGoldColorVisibility();
      recompute();
      // GA4 custom event — feeds Audiencia #4 (Low Budget) discrimination
      // DO NOT rename `widget_source` back to `source`: `source` is a GA4 RESERVED
      // parameter (traffic-source attribution). Sending it on an event overwrites the
      // session source and dumps those users into the "Unassigned" channel.
      // Same for: medium, campaign, campaign_id, term, content, gclid, page_location,
      // page_referrer, and anything prefixed ga_ / google_ / firebase_.
      try { if (typeof gtag === 'function') gtag('event','metal_selected',{ metal: state.metal, widget_source: 'preset_configurator' }); } catch(_){}
    }));

    const goldColorPills = document.querySelectorAll('#egpc-gold-color-pills [data-gold-color]');
    goldColorPills.forEach(g => g.addEventListener('click', () => {
      goldColorPills.forEach(x => x.classList.remove('active'));
      g.classList.add('active');
      state.goldColor = g.dataset.goldColor;
      recompute();
    }));

    const platingPills = document.querySelectorAll('#egpc-plating-pills [data-plating]');
    const platingSection = document.getElementById('egpc-plating-section');
    function togglePlatingVisibility(){
      if (!platingSection) return;
      platingSection.style.display = (state.metal === 'Silver') ? 'block' : 'none';
      if (state.metal !== 'Silver') state.plating = 'none';
    }
    togglePlatingVisibility();
    platingPills.forEach(p => p.addEventListener('click', () => {
      platingPills.forEach(x => x.classList.remove('active'));
      p.classList.add('active');
      state.plating = p.dataset.plating;
      recompute();
    }));
    // Re-trigger on metal change so plating section toggles + resets
    metalPills.forEach(m => m.addEventListener('click', togglePlatingVisibility));

    // Fang size pills (only renders when has_fangs)
    const fangPills = document.querySelectorAll('#egpc-fang-pills [data-fang-size]');
    fangPills.forEach(f => f.addEventListener('click', () => {
      fangPills.forEach(x => x.classList.remove('active'));
      f.classList.add('active');
      state.fangSize = f.dataset.fangSize;
      recompute();
    }));

    // Gap Filler size pills (only renders when has_gaps)
    const gapPills = document.querySelectorAll('#egpc-gap-pills [data-gap-size]');
    gapPills.forEach(g => g.addEventListener('click', () => {
      gapPills.forEach(x => x.classList.remove('active'));
      g.classList.add('active');
      state.gapSize = g.dataset.gapSize;
      recompute();
    }));

    // Stone tiers (top-level): Moissanite / Lab Grown / Diamonds. "Diamonds" expands sub-grades.
    const stoneTierPills = document.querySelectorAll('#egpc-stone-tier-pills [data-stone-tier]');
    const diamondGrades = document.getElementById('egpc-diamond-grades');
    const diamondGradePills = document.querySelectorAll('#egpc-diamond-grade-pills [data-stone]');
    function setStoneTierActive(tier){
      stoneTierPills.forEach(x => x.classList.toggle('active', x.dataset.stoneTier === tier));
      state.stoneTier = tier;
      if (diamondGrades) diamondGrades.hidden = (tier !== 'diamond');
      if (tier === 'moissanite') state.stone = 'moissanite';
      else if (tier === 'lab-grown') state.stone = 'lab-grown';
      else if (tier === 'diamond') {
        // Default to SI when entering Diamonds; preserve sub-grade if already a diamond
        if (!String(state.stone || '').startsWith('diamond-')) {
          state.stone = 'diamond-si';
          diamondGradePills.forEach(x => x.classList.toggle('active', x.dataset.stone === 'diamond-si'));
        }
      }
    }
    stoneTierPills.forEach(t => t.addEventListener('click', () => {
      setStoneTierActive(t.dataset.stoneTier);
      recompute();
      // GA4 custom event — feeds Audiencia #4 (Low Budget = moissanite) + Audiencia #5 (Fancy = diamond-vvs) discrimination
      try { if (typeof gtag === 'function') gtag('event','stone_selected',{ stone: state.stone, tier: t.dataset.stoneTier, widget_source: 'preset_configurator' }); } catch(_){}
    }));
    // Diamond sub-grade pills
    diamondGradePills.forEach(g => g.addEventListener('click', () => {
      diamondGradePills.forEach(x => x.classList.remove('active'));
      g.classList.add('active');
      state.stone = g.dataset.stone;
      recompute();
      try { if (typeof gtag === 'function') gtag('event','stone_selected',{ stone: state.stone, tier: 'diamond', widget_source: 'preset_configurator' }); } catch(_){}
    }));

    const preciousPills = document.querySelectorAll('#egpc-precious-pills [data-precious]');
    preciousPills.forEach(p => p.addEventListener('click', () => {
      preciousPills.forEach(x => x.classList.remove('active'));
      p.classList.add('active');
      state.precious = p.dataset.precious;
      recompute();
    }));
    // Default precious stone: Opal (first option, per operator spec)
    if (preciousPills.length > 0 && !state.precious) {
      preciousPills[0].click();
    }

    // Lab/Genuine sub-toggle (only renders when has_precious; Genuine = 2× Lab price)
    const preciousTypePills = document.querySelectorAll('#egpc-precious-type-pills [data-precious-type]');
    preciousTypePills.forEach(p => p.addEventListener('click', () => {
      preciousTypePills.forEach(x => x.classList.remove('active'));
      p.classList.add('active');
      state.preciousType = p.dataset.preciousType;
      recompute();
    }));

    // Nugget size pills (only renders when has_nuggets) — Mini/Regular/Bulky
    const nuggetPills = document.querySelectorAll('#egpc-nugget-pills [data-nugget]');
    nuggetPills.forEach(n => n.addEventListener('click', () => {
      nuggetPills.forEach(x => x.classList.remove('active'));
      n.classList.add('active');
      state.nuggetSize = n.dataset.nugget;
      recompute();
    }));

    // Diamond Cut pattern pills (only renders when has_diamond_cut) — visual only, $0 uplift
    const cutPills = document.querySelectorAll('#egpc-cut-pattern-pills [data-cut-pattern]');
    cutPills.forEach(c => c.addEventListener('click', () => {
      cutPills.forEach(x => x.classList.remove('active'));
      c.classList.add('active');
      state.cutPattern = c.dataset.cutPattern;
      recompute();
    }));

    // Single Tooth sub-style pills (only renders when has_single_only) — Solid/Diamond Cut/Iced
    const substylePills = document.querySelectorAll('#egpc-substyle-pills [data-substyle]');
    substylePills.forEach(s => s.addEventListener('click', () => {
      substylePills.forEach(x => x.classList.remove('active'));
      s.classList.add('active');
      state.substyle = s.dataset.substyle;
      recompute();
    }));

    // Teeth picker — visibility driven by state.position.
    // Full = both arches visible. Single = top arch only (customer picks 1 tooth).
    function syncTeethArchVisibility(){
      const topArch = document.querySelector('[data-arch-container="top"]');
      const botArch = document.querySelector('[data-arch-container="bottom"]');
      const showTop = state.position === 'Top' || state.position === 'Full' || state.position === 'Single';
      const showBot = state.position === 'Bottom' || state.position === 'Full';
      if (topArch) topArch.hidden = !showTop;
      if (botArch) botArch.hidden = !showBot;
    }
    function clearTeethSelection(){
      state.teeth.clear();
      document.querySelectorAll('.egpc-tooth.active').forEach(t => t.classList.remove('active'));
    }
    syncTeethArchVisibility();
    // When Position changes, reset teeth (previous selections don't apply to the new arch)
    positionPills.forEach(p => p.addEventListener('click', () => {
      clearTeethSelection();
      syncTeethArchVisibility();
      recompute();
    }));
    // Tooth click toggles selection — respect max cap (dynamic for Configurable Caps).
    const teethBtns = document.querySelectorAll('.egpc-tooth');
    teethBtns.forEach(t => t.addEventListener('click', () => {
      const id = t.dataset.tooth;
      const maxCaps = maxCapsForPosition();
      if (state.teeth.has(id)) {
        state.teeth.delete(id);
        t.classList.remove('active');
      } else if (state.teeth.size < maxCaps) {
        state.teeth.add(id);
        t.classList.add('active');
      } else {
        // Already at cap — flash the counter as a hint
        const counter = document.getElementById('egpc-teeth-counter');
        if (counter){ counter.style.opacity = '0.4'; setTimeout(() => counter.style.opacity = '', 200); }
      }
      recompute();
    }));

    // Lightbox: open on image click / Enter / Space; close on X / outside click / Escape
    const imgBox = document.getElementById('egpc-imgbox');
    const lightbox = document.getElementById('egpc-lightbox');
    const lightboxImg = document.getElementById('egpc-lightbox-img');
    const lightboxClose = document.getElementById('egpc-lightbox-close');
    function openLightbox(){
      if (!imgBox || !lightbox || !lightboxImg) return;
      const srcImg = imgBox.querySelector('img');
      if (!srcImg) return;
      lightboxImg.src = srcImg.src;
      lightboxImg.alt = srcImg.alt || '';
      lightbox.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
    }
    function closeLightbox(){
      if (!lightbox) return;
      lightbox.setAttribute('hidden','');
      document.body.style.overflow = '';
    }
    if (imgBox) {
      imgBox.addEventListener('click', openLightbox);
      imgBox.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(); }
      });
    }
    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox) lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox && !lightbox.hasAttribute('hidden')) closeLightbox();
    });
  }

  function bindBreakdown(){
    const toggle = document.getElementById('egpc-bd-toggle');
    const content = document.getElementById('egpc-bd-content');
    const arrow = document.getElementById('egpc-bd-arrow');
    if (!toggle || !content) return;
    toggle.addEventListener('click', () => {
      const open = content.classList.toggle('open');
      if (arrow) arrow.textContent = open ? '▴' : '▾';
    });
  }

  // ---------- toast ----------
  function toast(msg, isError){
    const safeMsg = (msg && String(msg).trim()) || 'Something went wrong. Check console for details.';
    console.log('[egpc] toast:', safeMsg, isError ? '(error)' : '');
    const el = document.getElementById('egpc-toast');
    if (!el) { alert(safeMsg); return; } // fallback if toast element somehow missing
    el.textContent = safeMsg;
    el.classList.toggle('error', !!isError);
    el.classList.add('show');
    setTimeout(() => el.classList.remove('show'), 6000);
  }

  // ---------- backend integration ----------
  async function postJSON(url, payload){
    const res = await fetch(url, {
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify(payload),
      credentials:'same-origin',
      cache:'no-store'
    });
    let body;
    try { body = await res.clone().json(); } catch { body = await res.text(); }
    if (!res.ok || (body && body.ok === false)){
      const err = new Error('HTTP ' + res.status);
      err.status = res.status; err.response = body;
      throw err;
    }
    return body;
  }

  function gidToLegacy(gid){
    if (!gid) return null;
    const m = String(gid).match(/\/ProductVariant\/(\d+)/);
    return m ? m[1] : null;
  }

  async function addToCart(variantId, properties){
    // Auto-retry on 422 "sold out". Race condition: a freshly-created variant with
    // inventoryItem.tracked=false sometimes still trips Shopify's cart-add inventory
    // check on the first attempt because the tracked-state cache hasn't fully
    // propagated. A brief wait (900ms) + one retry resolves it transparently.
    const doRequest = async () => {
      const res = await fetch('/cart/add.js', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        credentials:'same-origin',
        body:JSON.stringify({ id:String(variantId), quantity:1, properties })
      });
      const body = await res.text();
      return { res, body };
    };

    let { res, body } = await doRequest();
    if (!res.ok && res.status === 422 && /sold out/i.test(body)){
      console.log('[egpc] cart/add transient sold-out; retrying in 2000ms…');
      await new Promise(r => setTimeout(r, 2000));
      ({ res, body } = await doRequest());
    }
    if (!res.ok && res.status === 422 && /sold out/i.test(body)){
      console.log('[egpc] cart/add still sold out; second retry in 2500ms…');
      await new Promise(r => setTimeout(r, 2500));
      ({ res, body } = await doRequest());
    }
    if (!res.ok){
      throw new Error('cart/add.js error: ' + body);
    }
    return JSON.parse(body);
  }

  // ---------- loading modal ----------
  // Branded overlay that covers the create-variant + cart-add window (~3-5s).
  // Rotates step text + tip cards to make the wait feel intentional, not slow.
  const LOADING_STEPS = [
    'Reserving your design',
    'Crafting your variant',
    'Preparing your checkout',
  ];
  const LOADING_TIPS = [
    { label: 'How it works', text: 'After checkout, we mail you a mold kit to take your dental impression at home.' },
    { label: 'Step 2', text: 'Once we confirm your mold fits perfectly, we begin crafting your piece.' },
    { label: 'Timing', text: 'Solid gold grillz ship in 7–12 business days from mold confirmation.' },
    { label: 'Timing', text: 'Diamond & stone pieces: up to 2 weeks of meticulous setting.' },
    { label: 'Timing', text: 'Custom enamel: up to 4 weeks depending on complexity.' },
    { label: 'Shipping', text: 'We ship the moment your piece is finished — no waiting once it\'s ready.' },
  ];
  let loadingTipTimer = null;
  let loadingStepTimer = null;
  function showLoadingModal(){
    const modal = document.getElementById('egpc-loading-modal');
    if (!modal) return;
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    // Reset + animate progress bar over ~4s (gives margin if backend slow)
    const bar = document.getElementById('egpc-loading-bar');
    if (bar){
      bar.style.width = '0%';
      requestAnimationFrame(() => { bar.style.transition = 'width 4s ease-out'; bar.style.width = '95%'; });
    }
    // Rotate step text every ~1.3s
    let stepIdx = 0;
    setText('egpc-loading-step', LOADING_STEPS[0]);
    loadingStepTimer = setInterval(() => {
      stepIdx = (stepIdx + 1) % LOADING_STEPS.length;
      setText('egpc-loading-step', LOADING_STEPS[stepIdx]);
    }, 1300);
    // Rotate tip cards every ~2s with fade
    let tipIdx = Math.floor(Math.random() * LOADING_TIPS.length);
    function showTip(i){
      const labelEl = document.querySelector('.egpc-tip-label');
      const textEl = document.getElementById('egpc-tip-text');
      if (!textEl) return;
      textEl.classList.add('fading');
      setTimeout(() => {
        if (labelEl) labelEl.textContent = LOADING_TIPS[i].label;
        textEl.textContent = LOADING_TIPS[i].text;
        textEl.classList.remove('fading');
      }, 250);
    }
    showTip(tipIdx);
    loadingTipTimer = setInterval(() => {
      tipIdx = (tipIdx + 1) % LOADING_TIPS.length;
      showTip(tipIdx);
    }, 2000);
  }
  function hideLoadingModal(){
    const modal = document.getElementById('egpc-loading-modal');
    if (!modal) return;
    const bar = document.getElementById('egpc-loading-bar');
    if (bar){ bar.style.transition = 'width .2s ease-out'; bar.style.width = '100%'; }
    if (loadingTipTimer) clearInterval(loadingTipTimer);
    if (loadingStepTimer) clearInterval(loadingStepTimer);
    setTimeout(() => {
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }, 200);
  }

  let inFlight = false;
  async function onAddClick(){
    if (inFlight) return;
    const btn = document.getElementById('egpc-add-btn');
    // Not ready → scroll to the first missing section instead of submitting.
    if (btn.getAttribute('aria-disabled') === 'true') {
      const targetId = btn.dataset.scrollTarget || '';
      const target = targetId ? (document.getElementById(targetId) || document.querySelector('[data-arch-container]')) : null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('egpc-flash');
        setTimeout(() => target.classList.remove('egpc-flash'), 1400);
      }
      return;
    }
    inFlight = true; btn.disabled = true; btn.textContent = 'Crafting your piece…';
    showLoadingModal();

    const t0 = performance.now();
    const timing = {};

    try {
      const c = state._calcCache || {};
      const title = `${state.position} • ${karatKey(state.metal)} • ${c.caps} caps`;
      const properties = {
        DynamicVariant: 'true',
        Position: state.position,
        Metal: karatKey(state.metal),
        Caps: String(c.caps),
        ...(state.metal !== 'Silver' ? { GoldColor: state.goldColor } : {}),
        ...(state.metal === 'Silver' && state.plating === 'yellow' ? { Plating: 'Yellow Gold' } : {}),
        ...(window.__HAS_FANGS__ ? { FangSize: fangLabel(state.fangSize), FangCount: String(c.fangCount || 0) } : {}),
        ...(window.__HAS_GAPS__ ? { GapFillerSize: gapLabel(state.gapSize), GapFillerCount: String(c.gapCount || 0) } : {}),
        ...(window.__HAS_STONES__ && c.stoneCount > 0 ? { StoneType: stoneLabel(state.stone), StoneCount: String(c.stoneCount) } : {}),
        ...(state.precious ? {
          PreciousStone: state.precious,
          PreciousStoneType: state.preciousType === 'genuine' ? 'Genuine' : 'Lab',
          PreciousStoneCount: String(c.preciousCount || 0),
        } : {}),
        ...(c.customTier > 0 ? { CraftsmanshipLevel: String(c.customTier) } : {}),
        ...(window.__HAS_NUGGETS__ ? { NuggetSize: state.nuggetSize } : {}),
        ...(window.__HAS_DIAMOND_CUT__ && state.cutPattern ? { DiamondCutPattern: state.cutPattern } : {}),
        ...(window.__HAS_SUBSTYLE__ ? { Style: state.substyle } : {}),
        ...(state.teeth.size > 0 ? { TeethPositions: Array.from(state.teeth).join(', ') } : {}),
        CalcTotal: c.total.toFixed(2),
      };

      const payload = {
        productId: window.__PRODUCT_ID__,
        title,
        options: [state.position, karatKey(state.metal), String(c.caps)],
        price: c.total,
        caps: c.caps,
        gold: karatKey(state.metal),
        position: state.position,
        properties,
        ttlMinutes: 60,
      };

      // Only call through Shopify App Proxy — backend now requires HMAC-signed requests.
      // Direct Render URL calls would fail with 401 (no signature). Proxy URL is reliable.
      const appProxyUrl = window.__APP_PROXY_PATH__ || '/apps/create-variant';

      let resp;
      const tCreateStart = performance.now();
      resp = await postJSON(appProxyUrl, payload);
      timing.createVariant = Math.round(performance.now() - tCreateStart);

      let variantId = null;
      if (resp){
        if (resp.variantLegacyId) variantId = String(resp.variantLegacyId);
        else if (resp.variantId) variantId = String(resp.variantId);
        else if (resp.variantGid) variantId = gidToLegacy(resp.variantGid);
      }
      if (!variantId) throw new Error('Server did not return a variant ID');

      const tCartStart = performance.now();
      const cartResp = await addToCart(variantId, properties);
      timing.cartAdd = Math.round(performance.now() - tCartStart);
      timing.total = Math.round(performance.now() - t0);
      console.log('[egpc] ⏱️  TIMING:', JSON.stringify(timing), 'ms');
      console.log('[egpc] cart/add success, response:', cartResp);

      // Redirect to cart. Use window.top to escape Shopify Theme Editor iframe preview.
      // If we can't reach top (cross-origin), fall back to window.location.
      const cartUrl = (window.Shopify && Shopify.routes && Shopify.routes.root)
        ? Shopify.routes.root + 'cart?added=1'
        : '/cart?added=1';
      try {
        if (window.top && window.top !== window.self){
          window.top.location.href = cartUrl;
        } else {
          window.location.href = cartUrl;
        }
      } catch(e){
        // Cross-origin frame: fall back to local navigation
        window.location.href = cartUrl;
      }

    } catch (err) {
      console.error('[egpc] Add-to-cart error:', err);
      console.error('[egpc] err.response:', err?.response);
      let msg = '';
      if (err?.response?.error) msg = typeof err.response.error === 'string' ? err.response.error : JSON.stringify(err.response.error);
      else if (err?.response?.message) msg = String(err.response.message);
      else if (err?.message) msg = String(err.message);
      msg = msg || 'Could not add to cart. Open browser Console (F12) for details.';
      hideLoadingModal();
      toast(msg, true);
      btn.disabled = false;
      btn.textContent = 'Try again';
    } finally {
      inFlight = false;
    }
  }

  // ---------- init ----------
  ready(() => {
    if (!window.PRICING_MATRIX) console.warn('[egpc] PRICING_MATRIX missing; using fallback');

    // Warm up Shopify cart session — initializes _shopify_cart and _shopify_y/_s cookies.
    // Without this, the very FIRST cart-add of a brand-new session can fail with 422
    // "sold out" even when the variant has inventoryQuantity > 0 and CONTINUE policy.
    // Reproduced consistently in incognito: 1st add fails, 2nd works. Fires & forgets.
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(() => console.log('[egpc] cart session warmed'))
      .catch(() => {/* harmless if it fails */});

    bindPills();
    bindBreakdown();
    // Try to restore previous selections from localStorage (per-product) — keeps
    // customer's prior config when they navigate away and come back. 7-day TTL.
    const restored = restoreState();
    if (restored) {
      applyRestoredUiState();
    } else {
      // No prior state — sync from initial DOM-active buttons (Liquid defaults).
      const initPos = document.querySelector('#egpc-position-pills .egpc-pill.active');
      if (initPos && initPos.dataset.position) state.position = initPos.dataset.position;
      const initNug = document.querySelector('#egpc-nugget-pills .egpc-stone-tier.active');
      if (initNug && initNug.dataset.nugget) state.nuggetSize = initNug.dataset.nugget;
      const initCut = document.querySelector('#egpc-cut-pattern-pills .egpc-pgem.active');
      if (initCut && initCut.dataset.cutPattern) state.cutPattern = initCut.dataset.cutPattern;
      const initSub = document.querySelector('#egpc-substyle-pills .egpc-stone-tier.active');
      if (initSub && initSub.dataset.substyle) state.substyle = initSub.dataset.substyle;
      // Pre-select default teeth (fresh visit only). Still fully configurable — the
      // tooth click handlers and +/- limits are untouched; this just seeds state.teeth.
      const teethPlan = defaultTeethPlan();
      if (teethPlan && teethPlan.count){
        if (!state.position) state.position = teethPlan.position;
        document.querySelectorAll('#egpc-position-pills .egpc-pill').forEach(p =>
          p.classList.toggle('active', p.dataset.position === state.position));
        // Arch visibility inline (avoid depending on a nested-scope helper).
        var _showTop = state.position === 'Top' || state.position === 'Full' || state.position === 'Single';
        var _showBot = state.position === 'Bottom' || state.position === 'Full';
        var _topArch = document.querySelector('[data-arch-container="top"]');
        var _botArch = document.querySelector('[data-arch-container="bottom"]');
        if (_topArch) _topArch.hidden = !_showTop;
        if (_botArch) _botArch.hidden = !_showBot;
        const cap = maxCapsForPosition();
        const ids = orderedTeeth(state.position, Math.min(teethPlan.count, cap));
        state.teeth = new Set(ids);
        document.querySelectorAll('.egpc-tooth').forEach(t =>
          t.classList.toggle('active', state.teeth.has(t.dataset.tooth)));
      }
    }
    const btn = document.getElementById('egpc-add-btn');
    if (btn) btn.addEventListener('click', onAddClick);
    const sbBtn = document.getElementById('egpc-stickybar-btn');
    if (sbBtn) sbBtn.addEventListener('click', onAddClick);
    recompute();
    // After DOM is ready and the price element exists, attach a debounced save.
    const priceEl = document.getElementById('egpc-total');
    if (priceEl && window.MutationObserver) {
      let saveDebounce;
      const observer = new MutationObserver(() => {
        clearTimeout(saveDebounce);
        saveDebounce = setTimeout(saveState, 150);
      });
      observer.observe(priceEl, { childList: true, characterData: true, subtree: true });
    }
  });

  // ---------- persistence (per-product) ----------
  const STORAGE_KEY = 'egpc_state_' + (window.__PRODUCT_HANDLE__ || 'unknown');
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  function saveState() {
    try {
      const snapshot = {
        ts: Date.now(),
        position: state.position, metal: state.metal, goldColor: state.goldColor,
        plating: state.plating, fangSize: state.fangSize, gapSize: state.gapSize,
        stone: state.stone, stoneTier: state.stoneTier,
        precious: state.precious, preciousType: state.preciousType,
        teeth: Array.from(state.teeth),
        nuggetSize: state.nuggetSize, cutPattern: state.cutPattern, substyle: state.substyle,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (e) { /* localStorage may be full/blocked; non-fatal */ }
  }

  function restoreState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const s = JSON.parse(raw);
      if (!s || !s.ts) return false;
      if (Date.now() - s.ts > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      if (s.position) state.position = s.position;
      if (s.metal) state.metal = s.metal;
      if (s.goldColor) state.goldColor = s.goldColor;
      if (s.plating) state.plating = s.plating;
      if (s.fangSize) state.fangSize = s.fangSize;
      if (s.gapSize) state.gapSize = s.gapSize;
      if (s.stone) state.stone = s.stone;
      if (s.stoneTier) state.stoneTier = s.stoneTier;
      if (s.precious) state.precious = s.precious;
      if (s.preciousType) state.preciousType = s.preciousType;
      if (Array.isArray(s.teeth)) state.teeth = new Set(s.teeth);
      if (s.nuggetSize) state.nuggetSize = s.nuggetSize;
      if (s.cutPattern) state.cutPattern = s.cutPattern;
      if (s.substyle) state.substyle = s.substyle;
      return true;
    } catch (e) { return false; }
  }

  function applyRestoredUiState() {
    function setActive(selector, attr, value) {
      document.querySelectorAll(selector).forEach(el => {
        el.classList.toggle('active', el.dataset[attr] === value);
      });
    }
    setActive('#egpc-position-pills [data-position]', 'position', state.position);
    setActive('#egpc-metal-pills [data-metal]', 'metal', state.metal);
    setActive('#egpc-gold-color-pills [data-gold-color]', 'goldColor', state.goldColor);
    setActive('#egpc-plating-pills [data-plating]', 'plating', state.plating);
    setActive('#egpc-stone-tier-pills [data-stone-tier]', 'stoneTier', state.stoneTier);
    setActive('#egpc-precious-pills [data-precious]', 'precious', state.precious);
    setActive('#egpc-precious-type-pills [data-precious-type]', 'preciousType', state.preciousType);
    setActive('#egpc-nugget-pills [data-nugget]', 'nugget', state.nuggetSize);
    setActive('#egpc-cut-pattern-pills [data-cut-pattern]', 'cutPattern', state.cutPattern);
    setActive('#egpc-substyle-pills [data-substyle]', 'substyle', state.substyle);
    setActive('#egpc-fang-pills [data-fang-size]', 'fangSize', state.fangSize);
    setActive('#egpc-gap-pills [data-gap-size]', 'gapSize', state.gapSize);
    document.querySelectorAll('.egpc-tooth').forEach(t => {
      t.classList.toggle('active', state.teeth.has(t.dataset.tooth));
    });
    // Conditional section visibility (e.g. gold color hides for Silver)
    const goldColorSection = document.getElementById('egpc-gold-color-section');
    if (goldColorSection) goldColorSection.style.display = (state.metal === 'Silver') ? 'none' : 'block';
    const platingSection = document.getElementById('egpc-plating-section');
    if (platingSection) platingSection.style.display = (state.metal === 'Silver') ? 'block' : 'none';
    // Arch visibility based on position
    const topArch = document.querySelector('[data-arch-container="top"]');
    const botArch = document.querySelector('[data-arch-container="bottom"]');
    const showTop = state.position === 'Top' || state.position === 'Full' || state.position === 'Single';
    const showBot = state.position === 'Bottom' || state.position === 'Full';
    if (topArch) topArch.hidden = !showTop;
    if (botArch) botArch.hidden = !showBot;
  }

})();
